import type { FlattenedTranslations, Language } from "@aurem/translations";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInWeeks,
  format,
  formatDistanceToNow,
  isAfter,
  isToday,
  isValid,
  isYesterday,
  parseISO,
} from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { z } from "zod";

export const datePreprocess = (val: unknown) => {
  if (
    val === "" ||
    val === null ||
    val === undefined ||
    isNaN(new Date(val as string).getTime())
  )
    return undefined;
  return new Date(val as string);
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type OptionalZodDate = z.ZodType<Date | undefined, any, any>;

/**
 * Converts an ISO date string to relative time format (e.g., "2 hours ago", "1 day ago")
 * @param isoDateString - ISO date string (e.g., "2024-03-15T09:30:00Z")
 * @param locale - Language locale ("ar" or "en")
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (
  isoDateString: string | undefined,
  locale: Language = "en",
): string => {
  try {
    if (!isoDateString) {
      return "";
    }

    const date = parseISO(isoDateString);

    if (isNaN(date.getTime())) {
      return "";
    }

    const result = formatDistanceToNow(date, {
      addSuffix: true,
      includeSeconds: false,
      locale: locale === "ar" ? ar : enUS,
    });

    return result.charAt(0).toUpperCase() + result.slice(1);
  } catch (error) {
    return "";
  }
};

/**
 * Converts an ISO date string to minimal relative time format (e.g., "2m ago", "3h ago", "2M ago")
 * @param isoDateString - ISO date string (e.g., "2024-03-15T09:30:00Z")
 * @param t - Translation function from useTranslations hook
 * @returns Formatted minimal relative time string
 */
export const formatRelativeTimeMinimal = (
  isoDateString: string | undefined,
  t: (
    key: keyof FlattenedTranslations,
    params?: string | number | Date,
  ) => string,
): string => {
  try {
    if (!isoDateString) {
      return "";
    }

    const date = parseISO(isoDateString);

    if (isNaN(date.getTime())) {
      return "";
    }

    const now = new Date();
    const isFuture = isAfter(date, now);

    const days = differenceInDays(now, date);
    const weeks = differenceInWeeks(now, date);
    const hours = differenceInHours(now, date);
    const minutes = differenceInMinutes(now, date);

    if (Math.abs(weeks) >= 1) {
      return t("apps.admin.common.date", date);
    }

    // Time units configuration: [value, paramName, pastKey, futureKey]
    const timeUnits = [
      [
        days,
        "apps.admin.common.relativeTime.daysAgo",
        "apps.admin.common.relativeTime.inDays",
      ],
      [
        hours,
        "apps.admin.common.relativeTime.hoursAgo",
        "apps.admin.common.relativeTime.inHours",
      ],
      [
        minutes,
        "apps.admin.common.relativeTime.minutesAgo",
        "apps.admin.common.relativeTime.inMinutes",
      ],
    ] as const;

    for (const [value, pastKey, futureKey] of timeUnits) {
      if (Math.abs(value) >= 1) {
        return t(isFuture ? futureKey : pastKey, Math.abs(value));
      }
    }

    return t("apps.admin.common.relativeTime.justNow");
  } catch (error) {
    return "";
  }
};

const localeDatePartOrder = (() => {
  const probeDate = new Date(2000, 0, 2);
  const parts = new Intl.DateTimeFormat(navigator.language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(probeDate);
  return parts
    .filter((p) => p.type === "day" || p.type === "month" || p.type === "year")
    .map((p) => p.type);
})();

export const parseLocaleDate = (value: string): Date | null => {
  if (value.includes("-")) {
    const iso = new Date(value);
    if (!isNaN(iso.getTime())) return iso;
  }

  const separator = value.match(/[/.-]/)?.[0];
  if (!separator) return null;
  const segments = value.split(separator).map(Number);
  if (segments.length !== 3 || segments.some(isNaN)) return null;

  const mapped: Record<string, number> = {};
  localeDatePartOrder.forEach((type, i) => {
    mapped[type] = segments[i];
  });

  const date = new Date(mapped.year, mapped.month - 1, mapped.day);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Convert a date cell value to ISO (YYYY-MM-DD), locale-independent.
 * Accepts:
 *  - ISO strings (YYYY-MM-DD or full ISO datetime) — passed through as calendar date
 *  - DMY strings (dd/MM/yyyy, dd-MM-yyyy, dd.MM.yyyy) — parsed explicitly as day/month/year
 * Returns null for empty/invalid input.
 */
export const dmyToISODate = (
  value: string | undefined | null,
): string | null => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dmy = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, "0");
    const month = dmy[2].padStart(2, "0");
    let year = dmy[3];
    if (year.length === 2) year = (Number(year) >= 50 ? "19" : "20") + year;
    if (Number(month) < 1 || Number(month) > 12) return null;
    if (Number(day) < 1 || Number(day) > 31) return null;
    return `${year}-${month}-${day}`;
  }

  return toISODate(trimmed);
};

export const toISODate = (value: string | undefined): string | null => {
  if (!value) return null;
  const date = parseLocaleDate(value);
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Returns true when `v` is either a valid ISO date string (YYYY-MM-DD prefix,
 * as stored by the server or by formatDateToISO) or a fully typed dd/MM/yyyy
 * mask value (exactly 10 chars, stored via onRawBlur when clampOnBlur=false).
 */
export const isCompleteValidDate = (v: string): boolean => {
  if (!v) {
    return false;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    return !isNaN(new Date(v).getTime());
  }
  if (v.length === 10 && /^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split("/").map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    return (
      d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd
    );
  }
  return false;
};

/** Parses a value accepted by `isCompleteValidDate` into a Date, or null. */
export const parseCompleteDate = (v: string): Date | null => {
  if (!isCompleteValidDate(v)) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    return parseISODateAsLocal(v);
  }
  const [dd, mm, yyyy] = v.split("/").map(Number);
  return new Date(yyyy, mm - 1, dd);
};

/**
 * Parse ISO date string (YYYY-MM-DD) as a local calendar date
 * Prevents timezone shift issues with business dates
 * @param isoDateString - ISO date string (e.g., "1995-05-14")
 * @returns Date object representing the calendar date in local timezone
 */
export const parseISODateAsLocal = (isoDateString: string): Date => {
  const [year, month, day] = isoDateString.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a Date as an ISO calendar date string (YYYY-MM-DD), locale-independent.
 * Returns "" for undefined input. Used as the `formatDate` prop for date controls.
 */
export const formatDateToISO = (date: Date | undefined): string => {
  if (!date) {
    return "";
  }
  const dd = String(date.getDate()).padStart(2, "0");
  const MM = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${yyyy}-${MM}-${dd}`;
};

const detectFormatFromLocale = (): string => {
  const locale = navigator?.language || "";
  if (locale.startsWith("en-US")) return "MM/dd/yyyy";
  return "dd/MM/yyyy";
};

/**
 * Normalizes any date-like value into a JS Date, or null for empty/invalid input.
 * Handles Date instances, epoch numbers, ISO strings (appending `Z` when a time
 * component lacks a timezone), SumSub timestamps (`YYYY-MM-DD HH:MM:SS`, UTC) and
 * calendar-only `PlainDate` strings (parsed as local to avoid timezone shift).
 * Shared by `formatDate` and the `useFormatDate` display hook.
 */
export const parseDateValue = (
  date: Date | string | number | undefined | null,
): Date | null => {
  if (date === null || date === undefined || date === "") return null;

  if (date instanceof Date) return isValid(date) ? date : null;

  if (typeof date === "number") {
    const dateObj = new Date(date);
    return isValid(dateObj) ? dateObj : null;
  }

  let dateObj: Date;
  // Check for SumSub timestamp format (YYYY-MM-DD HH:MM:SS) - stored in UTC
  const isSumsubFormat = date.includes(" ") && !date.includes("T");

  if (isSumsubFormat) {
    // Convert SumSub format to ISO and append Z to indicate UTC
    dateObj = parseISO(date.replace(" ", "T") + "Z");
  } else {
    // Handle standard ISO formats
    let normalizedDate = date;

    const hasTime = /T\d{2}:\d{2}/.test(normalizedDate);
    const hasTimezone =
      normalizedDate.includes("Z") || /[+-]\d{2}:\d{2}$/.test(normalizedDate);

    if (hasTime && !hasTimezone) {
      normalizedDate = normalizedDate + "Z";
    }

    dateObj = parseISO(normalizedDate);

    if (!isValid(dateObj)) {
      dateObj = parseISODateAsLocal(normalizedDate);
    }
  }

  return isValid(dateObj) ? dateObj : null;
};

export const formatDate = (
  date: Date | string | undefined | null,
  formatString?: string,
): string => {
  const dateObj = parseDateValue(date);
  if (!dateObj) return "";

  const finalFormat = formatString || detectFormatFromLocale();
  return format(dateObj, finalFormat);
};

/**
 * Formats a timestamp with relative date (today/yesterday) or absolute date
 * @param date - Date object or ISO string
 * @param locale - Language locale ("ar" or "en")
 * @returns Formatted timestamp string (e.g., "Today, 2:30 PM" or "Mar 15, 2:30 PM")
 */
export const formatTimestamp = (
  date: Date | string,
  locale: Language = "en",
): string => {
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;

    if (!isValid(dateObj)) {
      return "";
    }

    const dateLocale = locale === "ar" ? ar : enUS;
    const time = format(dateObj, "h:mm a", { locale: dateLocale });

    if (isToday(dateObj) || isYesterday(dateObj)) {
      const rtf = new Intl.RelativeTimeFormat(
        locale === "ar" ? "ar" : "en-US",
        {
          numeric: "auto",
        },
      );
      const diffInDays = isToday(dateObj) ? 0 : -1;
      const relativeDay = rtf.format(diffInDays, "day");
      const capitalizedRelativeDay =
        relativeDay.charAt(0).toUpperCase() + relativeDay.slice(1);
      return `${capitalizedRelativeDay}, ${time}`;
    }

    return format(dateObj, "MMM d, h:mm a", { locale: dateLocale });
  } catch (error) {
    return "";
  }
};
